import { createContext, useContext, useState, useEffect } from "react";
import authAPI from "@/api/auth";
import { userAPI } from "@/api/users";
import { devLog, devError } from "@/lib/utils";
import {
  LANGUAGE_SESSION_OVERRIDE_KEY,
  useI18n,
} from "@/contexts/I18nContext";
import { useTabStore } from "@/stores/use-tab-store";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { setLanguage } = useI18n();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  devLog("AuthProvider - user:", user, "loading:", loading);

  useEffect(() => {
    devLog("AuthProvider useEffect running...");
    const initializeAuth = async () => {
      try {
        const accessToken = sessionStorage.getItem("accessToken");
        const savedUser = sessionStorage.getItem("user");

        if (accessToken && savedUser) {
          try {
            // 저장된 사용자 정보 파싱
            const parsedUser = JSON.parse(savedUser);

            // 먼저 저장된 사용자 정보로 상태 설정 (빠른 UI 응답)
            setUser(parsedUser);
            if (!sessionStorage.getItem(LANGUAGE_SESSION_OVERRIDE_KEY)) {
              setLanguage(parsedUser.language);
            }

            // 백그라운드에서 토큰 유효성 검증
            const userInfo = await userAPI.getMyProfile({
              showGlobalLoading: false,
            });

            // 서버에서 최신 사용자 정보 가져와서 업데이트
            if (userInfo.success && userInfo.data) {
              const updatedUser = userInfo.data;
              setUser(updatedUser);
              if (!sessionStorage.getItem(LANGUAGE_SESSION_OVERRIDE_KEY)) {
                setLanguage(updatedUser.language);
              }
              sessionStorage.setItem("user", JSON.stringify(updatedUser));
            }
          } catch (parseError) {
            devError("Failed to parse saved user:", parseError);
            clearAuthSessionLocal();
          }
        } else {
          devLog("No valid session found");
        }
      } catch (error) {
        devError("Auth initialization error:", error);
        clearAuthSessionLocal();
      } finally {
        setLoading(false);
      }
    };

    // 로컬 세션 정리 함수 (useEffect 내부용)
    const clearAuthSessionLocal = () => {
      setUser(null);
      useTabStore.getState().clearTabs();
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("user");
    };

    initializeAuth();
  }, [setLanguage]);

  const login = async (userId, password) => {
    setLoading(true);
    try {
      const response = await authAPI.login(userId, password);

      if (response.success) {
        useTabStore.getState().clearTabs();
        devLog(
          "Login successful, saving to sessionStorage:",
          response.data
        );

        // JWT 토큰 저장
        if (response.data.accessToken) {
          sessionStorage.setItem("accessToken", response.data.accessToken);
          devLog("Access token saved:", response.data.accessToken);
        }
        if (response.data.refreshToken) {
          sessionStorage.setItem("refreshToken", response.data.refreshToken);
          devLog("Refresh token saved:", response.data.refreshToken);
        }

        // 사용자 정보 저장
        let userData = response.data.user || response.data;
        try {
          const profileResponse = await userAPI.getMyProfile({
            showGlobalLoading: false,
          });
          userData = profileResponse.data || profileResponse;
        } catch (profileError) {
          devError("Failed to load profile after login:", profileError);
        }

        setUser(userData);
        sessionStorage.removeItem(LANGUAGE_SESSION_OVERRIDE_KEY);
        setLanguage(userData.language || "ko");
        sessionStorage.setItem("user", JSON.stringify(userData));
        devLog("User data saved:", userData);

        // 저장 확인
        devLog("SessionStorage check:", {
          accessToken: sessionStorage.getItem("accessToken"),
          refreshToken: sessionStorage.getItem("refreshToken"),
          user: sessionStorage.getItem("user"),
        });

        return { success: true };
      } else {
        return {
          success: false,
          error: response.message || "로그인에 실패했습니다.",
        };
      }
    } catch (error) {
      devError("Login error:", error);

      // API 에러 응답 처리
      if (error.response?.data?.message) {
        return { success: false, error: error.response.data.message };
      } else if (error.response?.status === 401) {
        return {
          success: false,
          error: "사용자아이디 또는 비밀번호가 잘못되었습니다.",
        };
      } else if (error.response?.status === 500) {
        return {
          success: false,
          error: "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        };
      } else if (
        error.code === "NETWORK_ERROR" ||
        error.message.includes("Network Error")
      ) {
        return { success: false, error: "네트워크 연결을 확인해주세요." };
      } else {
        return { success: false, error: "로그인 중 오류가 발생했습니다." };
      }
    } finally {
      setLoading(false);
    }
  };

  // 세션 정리 헬퍼 함수 (외부에서도 사용 가능)
  const clearAuthSession = () => {
    setUser(null);
    useTabStore.getState().clearTabs();
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem(LANGUAGE_SESSION_OVERRIDE_KEY);
  };

  const logout = async () => {
    try {
      // 서버에 로그아웃 요청
      await authAPI.logout();
    } catch (error) {
      devError("Logout error:", error);
    } finally {
      // 로컬 세션 정리
      clearAuthSession();
    }
  };

  // 세션 복원 함수 (수동으로 호출 가능)
  const restoreSession = async () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const savedUser = sessionStorage.getItem("user");

    if (accessToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);

        // 토큰 유효성 검증
        const userInfo = await userAPI.getMyProfile({
          showGlobalLoading: false,
        });
        if (userInfo.success && userInfo.data) {
          setUser(userInfo.data);
          if (!sessionStorage.getItem(LANGUAGE_SESSION_OVERRIDE_KEY)) {
            setLanguage(userInfo.data.language);
          }
          sessionStorage.setItem("user", JSON.stringify(userInfo.data));
        }
        return true;
      } catch (error) {
        clearAuthSession();
        return false;
      }
    }
    return false;
  };

  const value = {
    user,
    login,
    logout,
    loading,
    clearAuthSession,
    restoreSession,
    updateCurrentUser: (updates) => {
      setUser((current) => {
        const updated = { ...current, ...updates };
        sessionStorage.setItem("user", JSON.stringify(updated));
        return updated;
      });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
